import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, GripVertical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'textarea', label: 'Text Area' }
];

const TemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    fields: []
  });

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    } else {
      // Add default fields for new template
      setTemplate({
        name: '',
        description: '',
        fields: [
          { field_id: '1', field_name: 'name', field_type: 'text', label: 'Full Name', required: true, order: 1 },
          { field_id: '2', field_name: 'email', field_type: 'email', label: 'Email', required: true, order: 2 },
          { field_id: '3', field_name: 'phone', field_type: 'tel', label: 'Phone Number', required: false, order: 3 }
        ]
      });
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
    } catch (error) {
      toast.error('Failed to fetch template');
    }
  };

  const handleAddField = () => {
    const newField = {
      field_id: Date.now().toString(),
      field_name: `field_${Date.now()}`,
      field_type: 'text',
      label: 'New Field',
      required: false,
      order: template.fields.length + 1
    };
    setTemplate({ ...template, fields: [...template.fields, newField] });
  };

  const handleRemoveField = (fieldId) => {
    setTemplate({
      ...template,
      fields: template.fields.filter(f => f.field_id !== fieldId)
    });
  };

  const handleFieldChange = (fieldId, key, value) => {
    setTemplate({
      ...template,
      fields: template.fields.map(f =>
        f.field_id === fieldId ? { ...f, [key]: value } : f
      )
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (templateId) {
        await axios.put(`${API}/templates/${templateId}`, template, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/templates`, template, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template created');
      }
      navigate('/templates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/templates')}
          className="mb-6 text-stone-600 hover:text-[#1A4D2E]"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Templates
        </Button>

        <Card className="bg-white p-8 md:p-12 rounded-2xl card-shadow">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            {templateId ? 'Edit Template' : 'Create Template'}
          </h1>
          <p className="text-stone-600 mb-8">Customize the visitor sign-in form fields</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Template Name</Label>
              <Input
                data-testid="template-name"
                type="text"
                value={template.name}
                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
                placeholder="e.g., Luxury Properties, First Time Buyers"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Description (Optional)</Label>
              <Textarea
                data-testid="template-description"
                value={template.description}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                className="min-h-20 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg px-4 py-3"
                placeholder="Describe when to use this template"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500">Form Fields</Label>
                <Button
                  type="button"
                  onClick={handleAddField}
                  className="bg-[#D4A373] text-white rounded-full px-4 py-2 hover:bg-[#C89563]"
                  data-testid="add-field-button"
                >
                  <Plus size={16} className="mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-4">
                {template.fields.map((field, index) => (
                  <Card key={field.field_id} className="p-4 border border-stone-200" data-testid={`field-${index}`}>
                    <div className="flex items-start gap-4">
                      <div className="pt-3">
                        <GripVertical className="text-stone-400" size={20} />
                      </div>
                      
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-stone-500 mb-1 block">Field Label</Label>
                          <Input
                            type="text"
                            value={field.label}
                            onChange={(e) => handleFieldChange(field.field_id, 'label', e.target.value)}
                            className="h-10 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded px-3"
                            placeholder="e.g., Property Size"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-stone-500 mb-1 block">Field Name (ID)</Label>
                          <Input
                            type="text"
                            value={field.field_name}
                            onChange={(e) => handleFieldChange(field.field_id, 'field_name', e.target.value)}
                            className="h-10 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded px-3"
                            placeholder="property_size"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-stone-500 mb-1 block">Field Type</Label>
                          <select
                            value={field.field_type}
                            onChange={(e) => handleFieldChange(field.field_id, 'field_type', e.target.value)}
                            className="h-10 w-full bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded px-3"
                          >
                            {FIELD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>

                        {field.field_type === 'select' && (
                          <div className="md:col-span-3">
                            <Label className="text-xs text-stone-500 mb-1 block">Options (comma-separated)</Label>
                            <Input
                              type="text"
                              value={(field.options || []).join(', ')}
                              onChange={(e) => handleFieldChange(field.field_id, 'options', e.target.value.split(',').map(s => s.trim()))}
                              className="h-10 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded px-3"
                              placeholder="Option 1, Option 2, Option 3"
                            />
                          </div>
                        )}

                        <div className="md:col-span-2">
                          <Label className="text-xs text-stone-500 mb-1 block">Placeholder (optional)</Label>
                          <Input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => handleFieldChange(field.field_id, 'placeholder', e.target.value)}
                            className="h-10 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded px-3"
                          />
                        </div>

                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => handleFieldChange(field.field_id, 'required', e.target.checked)}
                              className="h-4 w-4 text-[#1A4D2E] border-stone-300 rounded focus:ring-[#1A4D2E]"
                            />
                            <span className="text-sm text-stone-600">Required</span>
                          </label>
                        </div>
                      </div>

                      <Button
                        type="button"
                        onClick={() => handleRemoveField(field.field_id)}
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 p-2"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/templates')}
                className="flex-1 border-2 border-[#1A4D2E] text-[#1A4D2E] bg-transparent rounded-full h-14 text-lg hover:bg-[#1A4D2E]/5"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-template"
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
              >
                {loading ? 'Saving...' : templateId ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default TemplateEditor;
